import { useEffect, useRef, useState, useMemo } from "react";
import type { SchemaField } from "../types";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import type { Review } from "@/modules/review/types";
import type { Brand, BrandKit } from "@/modules/brand/types";
import { useBrands } from "@/modules/brand";
import { getTextColorForBackground } from "@/lib/utils";
import VideoModal from "@/modules/review/components/video-modal";

// Editable field hook for managing contentEditable elements with placeholder support
function useEditableField(initialValue: string, configKey: string, onConfigChange: (key: string, value: unknown) => void, placeholder?: string) {
	const ref = useRef<HTMLDivElement>(null);
	const isEditingRef = useRef(false);
	const lastValueRef = useRef<string | null>(null);
	const previousPropValueRef = useRef<string>(initialValue);
	const [isEmpty, setIsEmpty] = useState(!initialValue.trim());

	// Initialize or sync content when ref becomes available or value changes
	useEffect(() => {
		if (!ref.current) return;

		// Initialize if not yet initialized
		if (lastValueRef.current === null) {
			ref.current.textContent = initialValue;
			lastValueRef.current = initialValue;
			previousPropValueRef.current = initialValue;
			setIsEmpty(!initialValue.trim());
			return;
		}

		// Check if the prop value actually changed (detects discard/reset)
		const propValueChanged = previousPropValueRef.current !== initialValue;
		const currentDomValue = ref.current.textContent || "";
		const domDiffersFromProp = currentDomValue !== initialValue;

		// Sync if:
		// - Not editing: sync whenever DOM differs from prop
		// - Editing: sync only if prop changed (handles discard) and DOM doesn't already match
		if ((!isEditingRef.current && domDiffersFromProp) || (isEditingRef.current && propValueChanged && domDiffersFromProp)) {
			ref.current.textContent = initialValue;
			lastValueRef.current = initialValue;
			previousPropValueRef.current = initialValue;
			setIsEmpty(!initialValue.trim());
		}
	}, [initialValue]);

	const handlers = {
		onFocus: () => {
			isEditingRef.current = true;
		},
		onBlur: () => {
			isEditingRef.current = false;
			const currentValue = ref.current?.textContent || "";
			setIsEmpty(!currentValue.trim());
		},
		onInput: (e: React.FormEvent<HTMLDivElement>) => {
			const newValue = e.currentTarget.textContent || "";
			onConfigChange(configKey, newValue);
			setIsEmpty(!newValue.trim());
		},
		onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === "Enter" && !e.shiftKey) {
				e.preventDefault();
				e.currentTarget.blur();
			}
		},
		onPaste: (e: React.ClipboardEvent) => {
			e.preventDefault();
			const text = e.clipboardData.getData("text/plain");
			document.execCommand("insertText", false, text);
		},
	};

	return { ref, handlers, isEmpty, placeholder };
}

// Helper functions
function getInitials(name: string) {
	return name
		.split(" ")
		.map((n) => n[0])
		.join("")
		.toUpperCase();
}

function getGradient(name: string) {
	const gradients = [
		"from-violet-500 to-purple-600",
		"from-blue-500 to-cyan-500",
		"from-emerald-500 to-teal-500",
		"from-orange-500 to-amber-500",
		"from-pink-500 to-rose-500",
		"from-indigo-500 to-blue-500",
	];
	const index = name.charCodeAt(0) % gradients.length;
	return gradients[index];
}

function hexToRgba(hex: string, alpha: number): string {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function AudioPlayer({ src, isDark, accentColor, initialDuration }: { src: string; isDark: boolean; accentColor: string; initialDuration?: number | null }) {
	const audioRef = useRef<HTMLAudioElement>(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(initialDuration || 0);

	const formatTime = (time: number) => {
		if (isNaN(time) || !isFinite(time)) return "0:00";
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, "0")}`;
	};

	const togglePlay = () => {
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const handleTimeUpdate = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0) {
			setCurrentTime(audioRef.current.currentTime);
			if (!duration) {
				setDuration(audioDur);
			}
		}
	};

	const handleLoadedMetadata = () => {
		if (!audioRef.current) return;
		const audioDur = audioRef.current.duration;
		if (isFinite(audioDur) && audioDur > 0 && !initialDuration) {
			setDuration(audioDur);
		}
	};

	const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
		const time = parseFloat(e.target.value);
		if (audioRef.current) {
			audioRef.current.currentTime = time;
			setCurrentTime(time);
		}
	};

	const handleEnded = () => {
		setIsPlaying(false);
		setCurrentTime(0);
	};

	const progress = duration > 0 && isFinite(duration) ? Math.min((currentTime / duration) * 100, 100) : 0;

	return (
		<div className={`mt-6 mb-4 p-3 rounded-xl ${isDark ? "bg-gray-500/30" : "bg-gray-100"}`}>
			<audio ref={audioRef} src={src} onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onEnded={handleEnded} />
			<div className="flex items-center gap-3">
				<button
					type="button"
					onClick={togglePlay}
					className={`w-8 h-8 flex items-center justify-center rounded-full shrink-0 shadow-sm ${isDark ? "text-white bg-gray-700" : "text-black bg-white"}`}
				>
					{isPlaying ? (
						<svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
							<path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
						</svg>
					) : (
						<svg className="w-4 h-4 ml-0.5" fill="currentColor" viewBox="0 0 24 24">
							<path d="M8 5v14l11-7z" />
						</svg>
					)}
				</button>
				<div className="flex-1 flex items-center gap-2">
					<span className={`text-xs tabular-nums ${isDark ? "text-gray-400" : "text-gray-500"}`}>{formatTime(currentTime)}</span>
					<div className="flex-1 relative h-1.5 rounded-full overflow-hidden bg-gray-300/50">
						<div className="absolute left-0 top-0 h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: accentColor }} />
						<input type="range" min={0} max={duration || 0} value={currentTime} onChange={handleSeek} className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" />
					</div>
					{duration >= 1 && <span className={`text-xs tabular-nums ${isDark ? "text-gray-400" : "text-gray-500"}`}>{formatTime(duration)}</span>}
				</div>
			</div>
		</div>
	);
}

function ReviewCardSkeleton({ isDark }: { isDark: boolean }) {
	return (
		<div className="break-inside-avoid w-full">
			<div className={`rounded-2xl p-5 border animate-pulse ${isDark ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"}`}>
				{/* Rating skeleton */}
				<div className="mb-3 flex gap-1">
					{[1, 2, 3, 4, 5].map((i) => (
						<div key={i} className={`w-4 h-4 rounded ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
					))}
				</div>
				{/* Content skeleton */}
				<div className="space-y-2 mb-4">
					<div className={`h-4 rounded w-full ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
					<div className={`h-4 rounded w-full ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
					<div className={`h-4 rounded w-3/4 ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
				</div>
				{/* Author skeleton */}
				<div className={`flex items-center gap-3 pt-3 border-t ${isDark ? "border-gray-700" : "border-gray-100"}`}>
					<div className={`w-10 h-10 rounded-full ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
					<div className="flex-1 space-y-2">
						<div className={`h-4 rounded w-24 ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
						<div className={`h-3 rounded w-32 ${isDark ? "bg-gray-700" : "bg-gray-200"}`} />
					</div>
				</div>
			</div>
		</div>
	);
}

type Theme = "light" | "dark" | "system-default";

interface DefaultTemplateProps {
	currentBrand: Brand | null;
	reviews: Review[];
	brandKit: BrandKit | null;
	title?: string;
	description?: string;
	contentConfig?: Record<string, unknown>;
	layoutConfig?: Record<string, unknown>;
	contentSchema?: Record<string, SchemaField>;
	onContentConfigChange: (key: string, value: unknown) => void;
	isLoading?: boolean;
	nextCursor?: string | null;
	onLoadMore?: () => Promise<void>;
	isLoadingMoreReviews?: boolean;
	resourceOptions?: Record<string, Array<{ value: string; label: string }>>;
}

export default function DefaultTemplate({
	currentBrand,
	reviews,
	brandKit,
	contentConfig,
	layoutConfig,
	contentSchema,
	onContentConfigChange,
	isLoading = false,
	nextCursor = null,
	onLoadMore,
	isLoadingMoreReviews = false,
	resourceOptions,
}: DefaultTemplateProps) {
	const { selectedBrand } = useBrands();
	const showBranding = selectedBrand?.branding !== "disabled";

	// Video modal state
	const [activeVideoPlaybackId, setActiveVideoPlaybackId] = useState<string | null>(null);

	// Helper to get placeholder from schema
	const getPlaceholder = (key: string): string | undefined => {
		return contentSchema?.[key]?.placeholder;
	};

	// Editable fields using the reusable hook
	const titleField = useEditableField((contentConfig?.title as string) || "", "title", onContentConfigChange, getPlaceholder("title"));
	const descriptionField = useEditableField((contentConfig?.description as string) || "", "description", onContentConfigChange, getPlaceholder("description"));
	const ctaButtonLabelField = useEditableField((contentConfig?.cta_button_label as string) || "", "cta_button_label", onContentConfigChange, getPlaceholder("cta_button_label"));
	const collectFeedbackTitleField = useEditableField(
		(contentConfig?.collect_feedback_title as string) || "",
		"collect_feedback_title",
		onContentConfigChange,
		getPlaceholder("collect_feedback_title"),
	);
	const collectFeedbackDescriptionField = useEditableField(
		(contentConfig?.collect_feedback_description as string) || "",
		"collect_feedback_description",
		onContentConfigChange,
		getPlaceholder("collect_feedback_description"),
	);
	const collectFeedbackCtaLabelField = useEditableField(
		(contentConfig?.collect_feedback_cta_label as string) || "",
		"collect_feedback_cta_label",
		onContentConfigChange,
		getPlaceholder("collect_feedback_cta_label"),
	);

	// Theme handling
	const theme = (layoutConfig?.theme as Theme) || "light";
	const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
		if (typeof window !== "undefined") {
			return window.matchMedia("(prefers-color-scheme: dark)").matches;
		}
		return false;
	});

	// Listen for system theme changes
	useEffect(() => {
		if (theme !== "system-default") return;

		const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
		const handler = (e: MediaQueryListEvent) => setSystemPrefersDark(e.matches);

		mediaQuery.addEventListener("change", handler);
		return () => mediaQuery.removeEventListener("change", handler);
	}, [theme]);

	// Calculate effective dark mode
	const isDark = useMemo(() => {
		if (theme === "dark") return true;
		if (theme === "light") return false;
		return systemPrefersDark; // system-default
	}, [theme, systemPrefersDark]);

	// Get logo URL with fallback priority
	const logoUrl = brandKit?.logo_icon_with_label_url || brandKit?.logo_icon_only_url || brandKit?.logo_label_only_url || null;

	// Get font families and weights
	const headingFont = brandKit?.font_heading_name || "Inter";
	const headingWeight = brandKit?.font_heading_weight || "700";
	const bodyFont = brandKit?.font_body_name || "Inter";
	const bodyWeight = brandKit?.font_body_weight || "400";

	// Get primary color
	const primaryColor = brandKit?.color_primary_hex || "#a855f7";
	const primaryColorWithOpacity20 = hexToRgba(primaryColor, 0.2);

	// Get accent color
	const accentColor = brandKit?.color_accent_hex || "#a855f7";
	const accentColorWithOpacity20 = hexToRgba(accentColor, 0.2);
	const accentColorWithOpacity80 = hexToRgba(accentColor, 0.8);
	const accentColorWithOpacity40 = hexToRgba(accentColor, 0.4);

	// Check if collect_feedback_form_id is valid against available forms
	const isFormMismatch = useMemo(() => {
		if (!layoutConfig?.collect_feedback) return false;
		const formId = contentConfig?.collect_feedback_form_id as string;
		if (!formId) return false; // No form selected yet, not a mismatch
		const formsOptions = resourceOptions?.forms || [];
		if (formsOptions.length === 0) return false; // Options still loading
		return !formsOptions.some((opt) => opt.value === formId);
	}, [layoutConfig?.collect_feedback, contentConfig?.collect_feedback_form_id, resourceOptions?.forms]);

	return (
		<div className={`min-h-[calc(100vh-49px)] ${isDark ? "bg-gray-900" : "bg-gray-50"}`} style={{ fontFamily: `"${bodyFont}", sans-serif` }}>
			<div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-6 sm:py-8 md:py-12">
				<div className="mb-8 sm:mb-10 md:mb-12 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 lg:gap-0">
					<div className="flex-1">
						{/* Logo */}
						{logoUrl ? (
							<div className="mb-4 sm:mb-5 md:mb-6">
								<img src={logoUrl} alt="Logo" className="h-8 sm:h-9 md:h-10 object-contain" />
							</div>
						) : currentBrand?.name ? (
							<div className="mb-4 sm:mb-5 md:mb-6">
								<h1 className={`text-xl sm:text-2xl font-bold ${isDark ? "text-white" : "text-primary"}`} style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight }}>
									{currentBrand.name}
								</h1>
							</div>
						) : null}
						<div className="grid mb-3 sm:mb-4" style={{ gridTemplateAreas: "'stack'" }}>
							{titleField.isEmpty && titleField.placeholder && (
								<span
									className={`text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight pointer-events-none px-2 -mx-2 py-1 ${isDark ? "text-gray-600" : "text-gray-300"}`}
									style={{ fontFamily: `"${headingFont}", sans-serif`, fontWeight: headingWeight, gridArea: "stack" }}
								>
									{titleField.placeholder}
								</span>
							)}
							<div
								ref={titleField.ref}
								contentEditable
								suppressContentEditableWarning
								className={`text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight outline-none focus:ring-0 border border-transparent bg-transparent cursor-text rounded-lg px-2 -mx-2 py-1 transition-colors ${
									isDark ? "hover:border-gray-600 focus:border-gray-500" : "hover:border-gray-300 focus:border-gray-400"
								} ${isDark ? "text-white caret-white" : "text-gray-900 caret-gray-900"}`}
								style={{ fontFamily: `"${headingFont}", sans-serif`, fontWeight: headingWeight, gridArea: "stack" }}
								{...titleField.handlers}
							/>
						</div>
						<div className="grid max-w-2xl" style={{ gridTemplateAreas: "'stack'" }}>
							{descriptionField.isEmpty && descriptionField.placeholder && (
								<span
									className={`text-base sm:text-lg leading-relaxed pointer-events-none px-2 -mx-2 py-1 ${isDark ? "text-gray-600" : "text-gray-300"}`}
									style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight, gridArea: "stack" }}
								>
									{descriptionField.placeholder}
								</span>
							)}
							<div
								ref={descriptionField.ref}
								contentEditable
								suppressContentEditableWarning
								className={`text-base sm:text-lg leading-relaxed outline-none focus:ring-0 border border-transparent bg-transparent cursor-text rounded-lg px-2 -mx-2 py-1 transition-colors ${
									isDark ? "hover:border-gray-600 focus:border-gray-500" : "hover:border-gray-300 focus:border-gray-400"
								} ${isDark ? "text-gray-400 caret-white" : "text-gray-600 caret-gray-600"}`}
								style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight, gridArea: "stack" }}
								{...descriptionField.handlers}
							/>
						</div>
						<div className={`mt-6 sm:mt-7 md:mt-8 ${layoutConfig?.cta ? "inline-grid" : "hidden"}`} style={{ gridTemplateAreas: "'stack'" }}>
							{ctaButtonLabelField.isEmpty && ctaButtonLabelField.placeholder && (
								<span
									className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-sm sm:text-base rounded-full font-medium pointer-events-none text-white/50 text-center z-10"
									style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight, gridArea: "stack" }}
								>
									{ctaButtonLabelField.placeholder}
								</span>
							)}
							<div
								ref={ctaButtonLabelField.ref}
								contentEditable
								suppressContentEditableWarning
								className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 text-sm sm:text-base rounded-full font-medium shadow-lg transition-all duration-200 hover:scale-105 text-center caret-white"
								style={{
									backgroundColor: accentColor,
									fontFamily: `"${bodyFont}", sans-serif`,
									fontWeight: bodyWeight,
									gridArea: "stack",
									minWidth: "120px",
									color: getTextColorForBackground(accentColor),
								}}
								{...ctaButtonLabelField.handlers}
							/>
						</div>
					</div>

					{/* Right side - Animated heart icon */}
					<div className="hidden lg:flex items-center justify-center shrink-0 lg:ml-8">
						<div className="relative">
							{/* Outer glow effect */}
							<div
								className="absolute inset-0 w-40 xl:w-56 h-40 xl:h-56 rounded-full blur-3xl animate-pulse"
								style={{
									animationDuration: "3s",
									backgroundColor: primaryColorWithOpacity20,
								}}
							/>
							{/* Right side - Creative speech bubble with quote */}
							<div className="hidden lg:flex items-center justify-center shrink-0 lg:ml-8">
								<div className="relative w-44 xl:w-60 h-44 xl:h-60">
									{/* Outer glow effect */}
									<div
										className="absolute inset-0 rounded-full blur-3xl animate-pulse"
										style={{
											animationDuration: "3s",
											backgroundColor: accentColorWithOpacity20,
										}}
									/>
									{/* Background bubble - offset for depth */}
									<div
										className="absolute top-4 left-4 w-32 xl:w-44 h-28 xl:h-40 rounded-3xl animate-pulse"
										style={{
											animationDuration: "3s",
											backgroundColor: accentColorWithOpacity20,
										}}
									/>
									{/* Main speech bubble */}
									<div
										className="absolute top-0 left-0 w-32 xl:w-44 h-28 xl:h-40 rounded-3xl animate-pulse flex items-center justify-center"
										style={{
											animationDuration: "2.5s",
											backgroundColor: accentColorWithOpacity40,
										}}
									>
										{/* Large quote mark */}
										<svg className="w-16 xl:w-24 h-16 xl:h-24" style={{ color: accentColor }} fill="currentColor" viewBox="0 0 24 24">
											<path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
										</svg>
									</div>
									{/* Bubble tail */}
									<div
										className="absolute bottom-6 xl:bottom-4 left-8 xl:left-10 w-6 xl:w-8 h-6 xl:h-8 rotate-45 animate-pulse"
										style={{
											animationDuration: "2.5s",
											backgroundColor: accentColorWithOpacity40,
										}}
									/>
									{/* Floating mini bubbles */}
									<div
										className="absolute top-2 right-4 w-4 xl:w-6 h-4 xl:h-6 rounded-full animate-bounce"
										style={{
											animationDuration: "2s",
											backgroundColor: accentColorWithOpacity80,
										}}
									/>
									<div
										className="absolute bottom-12 right-0 w-3 xl:w-4 h-3 xl:h-4 rounded-full animate-bounce"
										style={{
											animationDuration: "2.5s",
											animationDelay: "0.3s",
											backgroundColor: accentColorWithOpacity40,
										}}
									/>
									<div
										className="absolute top-1/2 right-2 w-2 xl:w-3 h-2 xl:h-3 rounded-full animate-bounce"
										style={{
											animationDuration: "1.8s",
											animationDelay: "0.6s",
											backgroundColor: accentColorWithOpacity80,
										}}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				{/* Testimonials Masonry Grid */}
				{isLoading ? (
					<ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 768: 2, 1024: 3 }}>
						<Masonry gutter="2rem">
							{[1, 2, 3, 4, 5, 6].map((i) => (
								<ReviewCardSkeleton key={i} isDark={isDark} />
							))}
						</Masonry>
					</ResponsiveMasonry>
				) : reviews.length === 0 ? (
					<div className={`py-12 sm:py-16 md:py-20 text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
						<div className="max-w-md mx-auto px-4">
							<svg className={`w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-4 ${isDark ? "text-gray-700" : "text-gray-300"}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={1.5}
									d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
								/>
							</svg>
							<h3
								className={`text-lg sm:text-xl font-semibold mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}
								style={{ fontFamily: `"${headingFont}", sans-serif`, fontWeight: headingWeight }}
							>
								No reviews yet
							</h3>
							<p
								className={`text-sm sm:text-base leading-relaxed ${isDark ? "text-gray-500" : "text-gray-600"}`}
								style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight }}
							>
								Check back soon to see what our customers are saying about us!
							</p>
						</div>
					</div>
				) : (
					<ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 768: 2, 1024: 3 }}>
						<Masonry gutter="2rem">
							{reviews.map((review) => {
								const customerInfo =
									review.customer_job_title && review.customer_company_name
										? `${review.customer_job_title} at ${review.customer_company_name}`
										: review.customer_headline || review.customer_company_name || "";

								return (
									<div className="w-full" key={review.id}>
										<div
											className={`group relative rounded-2xl p-5 transition-all duration-300 border ${
												isDark ? "bg-gray-800 hover:bg-gray-750 border-gray-700 hover:border-gray-600" : "bg-white hover:bg-gray-50 border-gray-200 hover:border-gray-300"
											}`}
										>
											<div className="absolute top-4 right-4 opacity-10 group-hover:opacity-20 transition-opacity">
												{review.content_type === "video" ? (
													<svg className={`w-8 h-8 ${isDark ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 14 14">
														<path
															stroke="currentColor"
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth="1"
															d="M1.14 9.084c0.167 1.435 1.36 2.566 2.8 2.697 0.996 0.09 2.018 0.167 3.06 0.167 1.042 0 2.064 -0.077 3.06 -0.167 1.44 -0.13 2.633 -1.262 2.8 -2.697 0.08 -0.682 0.14 -1.378 0.14 -2.084 0 -0.706 -0.06 -1.402 -0.14 -2.084 -0.167 -1.435 -1.36 -2.566 -2.8 -2.697 -0.996 -0.09 -2.018 -0.167 -3.06 -0.167 -1.042 0 -2.064 0.077 -3.06 0.167 -1.44 0.131 -2.633 1.262 -2.8 2.697A17.91 17.91 0 0 0 1 7c0 0.706 0.06 1.402 0.14 2.084Z"
														/>
														<path
															stroke="currentColor"
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth="1"
															d="M6.784 9.26C8.008 8.604 9.595 7.632 9.595 7c0 -0.632 -1.587 -1.604 -2.811 -2.26 -0.643 -0.344 -1.393 0.132 -1.393 0.861l0 2.798c0 0.729 0.75 1.205 1.393 0.86Z"
														/>
													</svg>
												) : review.content_type === "audio" ? (
													<svg className={`w-8 h-8 ${isDark ? "text-gray-500" : "text-gray-400"}`} fill="none" viewBox="0 0 14 14">
														<path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" d="M6.93848 11.3687v1.8819" strokeWidth="1" />
														<path
															stroke="currentColor"
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M6.98161 0.765137c1.54089 0 2.93578 0.917993 2.96889 2.235543 0.0562 2.23593 0.5798 5.78139 -2.93227 5.78139 -3.51209 0 -3.02508 -3.54546 -2.96889 -5.78139C4.08245 1.68313 5.44072 0.765137 6.98161 0.765137Z"
															strokeWidth="1"
														/>
														<path
															stroke="currentColor"
															strokeLinecap="round"
															strokeLinejoin="round"
															d="M1.41943 7.05933c0.18335 2.4085 2.18755 4.30967 5.54401 4.30967 3.14936 0 5.23766 -1.90649 5.61746 -4.30967"
															strokeWidth="1"
														/>
													</svg>
												) : (
													<svg className={`w-8 h-8 ${isDark ? "text-gray-500" : "text-gray-400"}`} fill="currentColor" viewBox="0 0 24 24">
														<path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
													</svg>
												)}
											</div>

											{/* Rating */}
											{review.rating > 0 && (
												<div className="mb-6">
													<div className="flex items-center gap-0.5">
														{[1, 2, 3, 4, 5].map((star) => (
															<svg
																key={star}
																className={`w-4 h-4 ${star <= review.rating ? "text-amber-400" : isDark ? "text-gray-600" : "text-gray-300"}`}
																fill={star <= review.rating ? "currentColor" : "none"}
																stroke="currentColor"
																viewBox="0 0 24 24"
															>
																<path
																	strokeLinecap="round"
																	strokeLinejoin="round"
																	strokeWidth={2}
																	d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
																/>
															</svg>
														))}
													</div>
												</div>
											)}

											{/* Video thumbnail */}
											{review.content_type === "video" && review.video_thumbnail_url && (
												<button
													type="button"
													onClick={() => review.mux_playback_id && setActiveVideoPlaybackId(review.mux_playback_id)}
													className="mb-4 -mx-1 -mt-1 overflow-hidden rounded-xl relative block w-[calc(100%+0.5rem)] cursor-pointer"
												>
													<img src={review.video_thumbnail_url} alt="" className="w-full h-40 object-cover transition-transform duration-300 group-hover:scale-105" />
													<div className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors">
														<div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center shadow-lg" style={{ backgroundColor: primaryColor }}>
															<svg className="w-5 h-5 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
																<path d="M8 5v14l11-7z" />
															</svg>
														</div>
													</div>
												</button>
											)}

											{/* Audio player */}
											{review.content_type === "audio" && review.audio_url && <AudioPlayer src={review.audio_url} isDark={isDark} accentColor={accentColor} initialDuration={review.audio_duration} />}

											{/* Content */}
											<p
												className={`text-sm leading-relaxed mb-4 ${isDark ? "text-gray-300" : "text-gray-600"}`}
												style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight }}
											>
												{review.content_text}
											</p>

											{/* Author info */}
											<div className={`flex items-center gap-3 pt-3 border-t ${isDark ? "border-gray-700" : "border-gray-100"}`}>
												{review.customer_avatar_url ? (
													<img src={review.customer_avatar_url} alt={review.customer_name} className="w-10 h-10 rounded-full object-cover" />
												) : (
													<div
														className={`w-10 h-10 rounded-full bg-linear-to-br ${getGradient(
															review.customer_name,
														)} flex items-center justify-center text-white text-xs font-semibold shadow-lg`}
													>
														{getInitials(review.customer_name)}
													</div>
												)}
												<div className="flex-1 min-w-0">
													<p
														className={`font-medium text-sm truncate ${isDark ? "text-white" : "text-gray-900"}`}
														style={{ fontFamily: `"${headingFont}", sans-serif`, fontWeight: headingWeight }}
													>
														{review.customer_name}
													</p>
													{customerInfo && (
														<p
															className={`text-xs truncate ${isDark ? "text-gray-400" : "text-gray-500"}`}
															style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight }}
														>
															{customerInfo}
														</p>
													)}
												</div>
											</div>
										</div>
									</div>
								);
							})}
						</Masonry>
					</ResponsiveMasonry>
				)}

				{/* Load More Button */}
				{!isLoading && nextCursor && (
					<div className={`flex justify-center py-6 sm:py-8 ${isDark ? "bg-gray-900" : ""}`}>
						<button
							type="button"
							onClick={onLoadMore}
							disabled={isLoadingMoreReviews}
							className={`inline-flex items-center gap-2 px-5 sm:px-6 py-2 sm:py-2.5 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
								isDark
									? "text-gray-300 bg-gray-800 border border-gray-700 hover:bg-gray-750 hover:text-white"
									: "text-tertiary bg-white border border-gray-200 hover:bg-gray-50 hover:text-primary"
							}`}
						>
							{isLoadingMoreReviews ? (
								<>
									<svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
										<circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
										<path
											className="opacity-75"
											fill="currentColor"
											d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
										/>
									</svg>
									Loading...
								</>
							) : (
								"Load More"
							)}
						</button>
					</div>
				)}

				{/* Footer CTA */}
				<div className={`mt-6 sm:mt-8 mb-12 sm:mb-16 text-center max-w-6xl mx-auto ${isDark ? "bg-gray-900" : ""} ${layoutConfig?.collect_feedback ? "" : "hidden"}`}>
					<div
						className={`inline-flex flex-col items-center gap-3 sm:gap-4 p-6 sm:p-8 rounded-xl sm:rounded-2xl border w-full sm:w-auto ${
							isDark ? "bg-gray-800" : "bg-white"
						} ${isFormMismatch ? "border-dashed border-amber-500" : isDark ? "border-gray-700" : "border-gray-200"}`}
					>
						{isFormMismatch && (
							<div className="flex flex-col items-center gap-1 text-amber-600 text-sm">
								<div className="flex items-center gap-2">
									<svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
										/>
									</svg>
									<span>The previously selected form is deleted or unavailable. This section will not be visible to the public.</span>
								</div>
								<span className="text-amber-500">Please select another "Collect Feedback Form" to update.</span>
							</div>
						)}
						<div className="grid w-full" style={{ gridTemplateAreas: "'stack'" }}>
							{collectFeedbackTitleField.isEmpty && collectFeedbackTitleField.placeholder && (
								<span
									className={`text-lg sm:text-xl font-semibold pointer-events-none px-2 py-1 text-center ${isDark ? "text-gray-600" : "text-gray-300"}`}
									style={{ fontFamily: `"${headingFont}", sans-serif`, fontWeight: headingWeight, gridArea: "stack" }}
								>
									{collectFeedbackTitleField.placeholder}
								</span>
							)}
							<div
								ref={collectFeedbackTitleField.ref}
								className={`text-lg sm:text-xl font-semibold outline-none focus:ring-0 border border-transparent bg-transparent cursor-text rounded-lg px-2 py-1 transition-colors text-center ${
									isDark ? "hover:border-gray-600 focus:border-gray-500" : "hover:border-gray-300 focus:border-gray-400"
								} ${isDark ? "text-white caret-white" : "text-gray-900 caret-gray-900"}`}
								style={{ fontFamily: `"${headingFont}", sans-serif`, fontWeight: headingWeight, gridArea: "stack" }}
								contentEditable
								suppressContentEditableWarning
								{...collectFeedbackTitleField.handlers}
							/>
						</div>
						<div className="grid max-w-md w-full" style={{ gridTemplateAreas: "'stack'" }}>
							{collectFeedbackDescriptionField.isEmpty && collectFeedbackDescriptionField.placeholder && (
								<span
									className={`text-sm sm:text-base pointer-events-none px-2 py-1 text-center ${isDark ? "text-gray-600" : "text-gray-300"}`}
									style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight, gridArea: "stack" }}
								>
									{collectFeedbackDescriptionField.placeholder}
								</span>
							)}
							<div
								ref={collectFeedbackDescriptionField.ref}
								className={`text-sm sm:text-base outline-none focus:ring-0 border border-transparent bg-transparent cursor-text rounded-lg px-2 py-1 transition-colors text-center ${
									isDark ? "hover:border-gray-600 focus:border-gray-500" : "hover:border-gray-300 focus:border-gray-400"
								} ${isDark ? "text-gray-400 caret-white" : "text-gray-600 caret-gray-600"}`}
								style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight, gridArea: "stack" }}
								contentEditable
								suppressContentEditableWarning
								{...collectFeedbackDescriptionField.handlers}
							/>
						</div>
						<div className="inline-grid justify-self-center" style={{ gridTemplateAreas: "'stack'" }}>
							{collectFeedbackCtaLabelField.isEmpty && collectFeedbackCtaLabelField.placeholder && (
								<span
									className="px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base font-medium pointer-events-none text-white/50 text-center z-10"
									style={{ fontFamily: `"${bodyFont}", sans-serif`, fontWeight: bodyWeight, gridArea: "stack" }}
								>
									{collectFeedbackCtaLabelField.placeholder}
								</span>
							)}
							<div
								ref={collectFeedbackCtaLabelField.ref}
								className="px-5 sm:px-6 py-2.5 sm:py-3 text-sm sm:text-base rounded-full font-medium shadow-lg transition-all duration-200 hover:scale-105 text-white text-center caret-white"
								style={{
									backgroundColor: accentColor,
									color: getTextColorForBackground(accentColor),
									fontFamily: `"${bodyFont}", sans-serif`,
									fontWeight: bodyWeight,
									gridArea: "stack",
									minWidth: "120px",
								}}
								contentEditable
								suppressContentEditableWarning
								{...collectFeedbackCtaLabelField.handlers}
							/>
						</div>
					</div>
				</div>

				{/* Powered by branding */}
				{showBranding && (
					<div className={`py-6 sm:py-8 flex justify-center text-center ${isDark ? "text-gray-500" : "text-gray-400"}`}>
						{/* <span className="text-sm">
							Powered by{" "}
							<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">
								Say About Us
							</a>
						</span> */}

						<a href="https://sayabout.us/" target="_blank" rel="noopener noreferrer" className="underline block hover:opacity-80">
							<img src="/images/badge-sayaboutus.svg" alt="build social proof with Say About Us" className="h-5 w-auto" />
						</a>
					</div>
				)}
			</div>

			{/* Video Modal */}
			{activeVideoPlaybackId && (
				<VideoModal open={!!activeVideoPlaybackId} onOpenChange={(open) => !open && setActiveVideoPlaybackId(null)} playbackId={activeVideoPlaybackId} />
			)}
		</div>
	);
}
